import React, { useState, useEffect } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import styles from './modelstepper.module.css';

const ModelStepper = ({ activeStep = 2 }) => {
  const [expandedStep, setExpandedStep] = useState<number | null>(activeStep);

  const steps = [
    {
      step: 1,
      title: 'Run Model',
      content: (
        <>
          <p>
            For pre-existing studies, the first step of the study process is populating the database
            with modelled data for the curent period.
          </p>
          <br />
          <h4>What is the model?</h4>
          <p>
            Much like the modelling in the spreadsheets the modelling in the research platform
            estimates a given company's total revenue and concentrations of business based on a
            combination of industry/region information from Reuters, publicly available revenue
            numbers, prior year study forecasts.
          </p>
          <br />
          <p>
            The model can be run with your choice of weighting factors, and it can be run a number
            of times in the modelling phase of the study. Once you have run the model and accepted
            it you can move to the Review Modelling Phase. Once you do this you can re-run the model
            for individual companies but not for the market as a whole.
          </p>
        </>
      ),
    },
    {
      step: 2,
      title: 'Review Modelling',
      content: (
        <>
          <p>There are two main tabs you can use to analyse and edit the study data:</p>
          <br />
          <h4>Companies:</h4>
          <p>
            The Company Analysis pane contains top-down analysis. Here you can see the output of the
            company top-down modelling, which uses financial information from Reuters in addition to
            the study segment forecasts for regions and industries. For each company you can adjust
            the total revenue number a variety of ways. You can re-run the model with a different
            set of inputs. If a supplier has not responded by the end of the study process and you
            do not have a good idea of the company's business, you may want to do this to capture
            the latest data from Reuters.
          </p>
          <br />
          <p>
            You can also revise the model by adjusting the growth rate using the slider. You can
            overwrite the modelled figure entirely. Finally, you can input a number directly from a
            supplier. When you make these changes you will see the market segmentation data change.
          </p>
          <br />
          <h4>Segments:</h4>
          <p>
            On the Segment Analysis pane you will find an overview of the market by segment. Each
            category row will summarize the changes for the market as a whole in that category.
            Beyond that you can drill into the category to see which companies are driving that
            change. At this level you can view the largest suppliers, the suppliers with the largest
            change, and the suppliers who are most exposed to the category. In this table you have
            the option to edit the concentrations of business for a company in a given segment.
          </p>
        </>
      ),
    },
    {
      step: 3,
      title: 'Generate Surveys and Meet with Suppliers',
      content: (
        <>
          <p>
            Once you have reviewed the model and become comfortable with the data, it is time to
            begin the outreach phase of the study process. In the outreach phase you will improve
            the quality of the modelled data by gathering supplier input on as many data points as
            possible.
          </p>
          <br />
          <h4>Briefings:</h4>
          <p>
            You should use the Research Platform to schedule all briefings with target companies.{' '}
            <i>Do not schedule meetings directly through Teams. Or email.</i>
            When you schedule a briefing you can add attendees from the target company and internal
            attendees. If you do not see the external attendee in the contact database please take a
            moment to add them. The app will not allow you to add attendees by email alone.
          </p>
          <br />
          <p>
            After a briefing you can add your briefing notes to the briefing record. You always find
            the briefings for a given company in the company table.
          </p>
          <br />
          <h4>Surveys:</h4>
          <p>For each company you can configure the desired pattern of outreach.</p>
          <br />
          <p>
            If you want to send a spreadsheet you can use the data export tool for that company and
            generate a survey spreadsheet. You can then send a link to this spreadsheet to the
            target company in Sharepoint. You can also export a file and send it directly.
          </p>
          <br />
          <p>
            You can also use the app to send the target company a survey directly. If you do so the
            app will automatically send the target contact a survey link and code that the contact
            can use to access the online survey. This survey will be hosted on the intranet and will
            use a secure one-time login. You can assure the contact of the security of our platform.
            The survey page will allow the contact to view charts and data alongside the input
            slots.
          </p>
        </>
      ),
    },
    {
      step: 4,
      title: 'Review the Forecast',
      content: (
        <>
          <p>
            A study forecast is always maintained for 5 years out of the present year. The study has
            one top line forecast value. This value may be derived from a combination of modelling
            using region and industry financials. It is stored as a percent value in the database
            for each year out from the present. The raw figures for market size in a given forecast
            year are dynamically calculated based on the last available figure in the database. This
            is because the present size of the study may change as the analyst inputs data.
          </p>
          <br />
          <p>
            The forecast values for each study category are also calculated dynamically because both
            the total study size, total study growth, and market segment size may change. The
            forecast values are stored as percentages of the total market that play dynamically with
            the total study forecast growth rate.
          </p>
        </>
      ),
    },
  ];

  useEffect(() => {
    setExpandedStep(activeStep);
  }, [activeStep]);

  const handleClick = (step: number) => {
    if (expandedStep !== step) {
      setExpandedStep(step);
    }
  };

  const getCardClassName = (step: number) => {
    let className = styles.card;
    if (step === activeStep) className += ` ${styles.cardActive}`;
    if (step < activeStep) className += ` ${styles.cardCompleted}`;
    if (step === expandedStep) className += ` ${styles.cardExpanded}`;
    return className;
  };

  //   const getStepIconClassName = (step: number) => {
  //     let className = styles.stepIcon;
  //     if (step === activeStep) className += ` ${styles.stepIconActive}`;
  //     if (step < activeStep) className += ` ${styles.stepIconCompleted}`;
  //     return className;
  //   };

  const getStepNumber = (num: number) => {
    return num < 10 ? `0${num}` : num;
  };

  return (
    <div className={styles.container}>
      {steps.map(({ step, title, content }) => (
        <div key={step} onClick={() => handleClick(step)} className={getCardClassName(step)}>
          <div className={styles.stepWrapper}>
            <div
              className={
                step === activeStep
                  ? styles.stepIconActive
                  : step < activeStep
                    ? styles.stepIconCompleted
                    : styles.stepIcon
              }
            >
              {getStepNumber(step)}
            </div>
            <h3
              className={
                step === activeStep
                  ? styles.titleActive
                  : step < activeStep
                    ? styles.titleCompleted
                    : styles.title
              }
            >
              {title}
            </h3>
          </div>

          {expandedStep === step && <div className={styles.content}>{content}</div>}
        </div>
      ))}
    </div>
  );
};

export default ModelStepper;
